import React from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { useAuth } from "../../../../contexts/AuthContext";
import {
  calculateRoundedIqdAmount,
  getLockedNetPriceSummary,
  formatRateSource,
  type FxLockInfo,
} from "../../../../utils/iqd";

interface OrderSummaryProps {
  formData: any;
  exchangeRate: number;
  /** Immutable net price in USD from the backend (locked when the order is fully paid). */
  netPriceUsd?: number | null;
  /** Immutable net price in IQD from the backend (locked when the order is fully paid). */
  netPriceInIqd?: number | null;
  /** FX-lock audit metadata from the backend. */
  fxLock?: (FxLockInfo & { isLocked?: boolean }) | null;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  formData,
  exchangeRate,
  netPriceUsd,
  netPriceInIqd,
  fxLock,
}) => {
  const { t } = useTranslation('orders');
  const { isRTL } = useLanguage();
  const { hasPermission } = useAuth();

  // Financial Control: once an order is fully paid its net price is immutable and must be read
  // from the locked invoice snapshot -- never recomputed from the current live exchange rate.
  // Prefer the backend's authoritative values, fall back to a local computation over the locked
  // invoices (covers historical responses without the fields), then to a live recalculation.
  const localLocked = getLockedNetPriceSummary(formData.invoices);
  const liveNetPrice = Math.max(
    0,
    Number(formData.totalPrice || 0) - Number(formData.voucherValue || 0),
  );
  const netPrice =
    netPriceUsd != null
      ? Number(netPriceUsd)
      : localLocked.netPriceUsd != null
        ? localLocked.netPriceUsd
        : liveNetPrice;
  const resolvedNetPriceInIqd =
    netPriceInIqd != null
      ? Number(netPriceInIqd)
      : localLocked.netPriceIqd != null
        ? localLocked.netPriceIqd
        : calculateRoundedIqdAmount(liveNetPrice, exchangeRate);
  const isLocked = (fxLock?.isLocked ?? localLocked.isFullyLocked) === true;
  const lockInfo: FxLockInfo | null = fxLock ?? localLocked.lock;
  const showFxLock = !!lockInfo && (isLocked || lockInfo.exchangeRate != null);

  return (
    <div>
      <div className="mb-3">
        <h5 className="mb-0">
          <i className={`fas fa-chart-line ${isRTL ? 'ms-2' : 'me-2'}`}></i>
          {t('summary.summaries')}
        </h5>
      </div>
      <div className="summary-content">
        {/* Detailed Summary - Protected by Permission */}
        {hasPermission("Display All Order Details") && (
          <div className="row g-4">
            {/* Order Price */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-receipt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.orderPrice', 'Order Price')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.orderPrice ?? "-"}
                </div>
              </div>
            </div>
            {/* Merchant Delivery Fee */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-receipt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('fields.merchantDeliveryFee')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.merchentDeliveryFee ?? "-"}
                </div>
              </div>
            </div>
            {/* Exchange Price */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-exchange-alt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.exchangePrice')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.exchangePrice ?? 0}
                </div>
              </div>
            </div>
            {/* Chosen Weight */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-weight ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.choosenWeight')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.diementionalWeight ?? 0}
                </div>
              </div>
            </div>
            {/* Shipping Cost */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-truck ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.shippingCost')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.shippingCost ?? 0}
                </div>
              </div>
            </div>
            {/* Clearance */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-file-invoice ${isRTL ? 'ms-2' : 'me-2'}`}></i>Clearance
                </label>
                <div className="fw-semibold fs-5">
                  {formData.clearance ?? 0}
                </div>
              </div>
            </div>
            {/* Shipping Insurance */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-shield-alt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.shippingInsurance')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.shippingInssurance ?? 0}
                </div>
              </div>
            </div>
            {/* Shipping Fuel Cost */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-gas-pump ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.fuelCost')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.shippingFuelCost ?? 0}
                </div>
              </div>
            </div>
            {/* Tax */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-percent ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.tax')}
                </label>
                <div className="fw-semibold fs-5">{formData.tax ?? 0}</div>
              </div>
            </div>
            {/* Buy For Me Cost */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-shopping-basket ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.buyForMeCost', 'Buy For Me')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.buyForMeCost ?? 0}
                </div>
              </div>
            </div>

            {/* Payment Method Cost */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-credit-card ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.paymentMethodFee', 'Payment Method Fee')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.paymentMethodCost ?? 0}
                </div>
              </div>
            </div>
            {/* DG Fees */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-exclamation-triangle ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('fields.dgFees')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.dgFees ?? 0}
                </div>
              </div>
            </div>
            {/* Local Delivery Fee */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-map-marker-alt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('fields.localDeliveryFee')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.localDeliveryFee ?? 0}
                </div>
              </div>
            </div>
            {/* Additional Cost */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-credit-card ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.additionalCost')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.additionaCost}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Totals - Always Visible */}
        <h2 className="mt-3">{t('summary.totals', 'Totals')}</h2>
          <div className="row g-4">
            {/* Discount */}
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-receipt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.discount')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.voucherValue ?? "-"}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-receipt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.servicePrice')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.servicePrice ?? "-"}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-receipt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.totalPrice', 'Total Price')}
                </label>
                <div className="fw-semibold fs-5">
                  {formData.totalPrice ?? "-"}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border rounded p-3 bg-light h-100">
                <label className="form-label text-muted">
                  <i className={`fas fa-receipt ${isRTL ? 'ms-2' : 'me-2'}`}></i>{t('summary.netPrice')}
                  {isLocked && (
                    <span className={`badge bg-success ${isRTL ? 'me-2' : 'ms-2'}`}>
                      <i className={`fas fa-lock ${isRTL ? 'ms-1' : 'me-1'}`}></i>
                      {t('summary.locked')}
                    </span>
                  )}
                </label>
                <div className="fw-semibold fs-5">
                  {Number(netPrice).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  <div className="small text-muted mt-1">
                    ({resolvedNetPriceInIqd.toLocaleString('en-US')} {t('units.iqd')})
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FX Lock / Audit - the exact rate frozen at payment time */}
          {showFxLock && lockInfo && (
            <div className="mt-4">
              <div className="border rounded p-3 bg-light">
                <label className="form-label text-muted mb-2">
                  <i className={`fas fa-lock ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                  {t('summary.fxLockTitle')}
                </label>
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="text-muted small">{t('summary.lockedRate')}</div>
                    <div className="fw-semibold">
                      {lockInfo.exchangeRate != null
                        ? Number(lockInfo.exchangeRate).toLocaleString('en-US', {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })
                        : '-'}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted small">{t('summary.rateSource')}</div>
                    <div className="fw-semibold">{formatRateSource(lockInfo.rateSource)}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted small">{t('summary.lockedAt')}</div>
                    <div className="fw-semibold">
                      {lockInfo.rateLockedAt
                        ? new Date(lockInfo.rateLockedAt).toLocaleString('en-CA')
                        : '-'}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted small">{t('summary.roundingRule')}</div>
                    <div className="fw-semibold">{lockInfo.roundingRule || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};
